import { createClient, SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// Check if Supabase is configured
export const isSupabaseConfigured = () => {
  return supabaseUrl && supabaseUrl.length > 0 && supabaseUrl !== 'undefined'
}

// Only create client if properly configured
export const supabase: SupabaseClient = isSupabaseConfigured() 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : createClient('https://placeholder.supabase.co', 'placeholder-key')

// Auth helpers
export const signInAnonymously = async () => {
  const { data, error } = await supabase.auth.signInAnonymously()
  return { user: data.user, error }
}

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  return user
}

export const onAuthStateChange = (callback: (user: any) => void) => {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(session?.user || null)
  })
}

// Leaderboard types
export interface LeaderboardEntry {
  id?: string
  user_id: string
  username: string
  emoji: string
  flag: string
  score: number
  game: string
  level: number
  created_at?: string
}

export interface UserProfile {
  id?: string
  user_id: string
  username: string
  emoji: string
  flag: string
  total_score: number
  resources: Record<string, number>
  created_at?: string
  updated_at?: string
}

// Leaderboard functions
export const submitScore = async (entry: Omit<LeaderboardEntry, 'id' | 'created_at'>) => {
  if (!isSupabaseConfigured()) {
    // Store locally if Supabase not configured
    console.log('[Supabase] Not configured, storing locally')
    const scores: LeaderboardEntry[] = JSON.parse(localStorage.getItem('planetmoji_scores') || '[]')
    
    // Find existing entry for this user+game
    const existingIdx = scores.findIndex(s => s.user_id === entry.user_id && s.game === entry.game)
    
    if (existingIdx >= 0) {
      // Only update if new score is higher
      if (entry.score > scores[existingIdx].score) {
        scores[existingIdx] = { ...entry, id: scores[existingIdx].id, created_at: new Date().toISOString() }
      }
    } else {
      scores.push({ ...entry, id: Date.now().toString(), created_at: new Date().toISOString() })
    }
    
    localStorage.setItem('planetmoji_scores', JSON.stringify(scores.slice(-100)))
    return { data: entry, error: null }
  }

  console.log('[Supabase] Checking existing score for:', entry.user_id, entry.game)
  
  // First check if there's an existing score for this user+game
  const { data: existingList, error: selectError } = await supabase
    .from('leaderboard')
    .select('id, score')
    .eq('user_id', entry.user_id)
    .eq('game', entry.game)
    .limit(1)
  
  const existing = existingList && existingList.length > 0 ? existingList[0] : null
  
  if (selectError) {
    console.error('[Supabase] Select error:', selectError.message)
  }
  
  if (existing) {
    // Only update if new score is higher
    if (entry.score > existing.score) {
      console.log('[Supabase] New high score! Updating:', existing.score, '->', entry.score)
      const { data, error } = await supabase
        .from('leaderboard')
        .update({ 
          score: entry.score, 
          level: entry.level,
          username: entry.username,
          emoji: entry.emoji,
          flag: entry.flag
        })
        .eq('id', existing.id)
        .select()
        .single()
      
      if (error) {
        console.error('[Supabase] Update error:', error.message)
      } else {
        console.log('[Supabase] Updated to new high score:', data)
      }
      return { data, error }
    } else {
      console.log('[Supabase] Score not higher than existing:', entry.score, '<=', existing.score)
      return { data: existing, error: null }
    }
  } else {
    // No existing entry, insert new
    console.log('[Supabase] No existing score, inserting new entry')
    const { data, error } = await supabase
      .from('leaderboard')
      .insert([entry])
      .select()
      .single()

    if (error) {
      console.error('[Supabase] Insert error:', error.message)
    } else {
      console.log('[Supabase] Insert success:', data)
    }
    return { data, error }
  }
}

export const getLeaderboard = async (game?: string, limit = 50) => {
  if (!isSupabaseConfigured()) {
    // Return local scores if Supabase not configured
    const scores = JSON.parse(localStorage.getItem('planetmoji_scores') || '[]')
    const filtered = game ? scores.filter((s: LeaderboardEntry) => s.game === game) : scores
    return { 
      data: filtered.sort((a: LeaderboardEntry, b: LeaderboardEntry) => b.score - a.score).slice(0, limit), 
      error: null 
    }
  }

  let query = supabase
    .from('leaderboard')
    .select('*')
    .order('score', { ascending: false })
    .limit(limit)

  if (game) {
    query = query.eq('game', game)
  }

  const { data, error } = await query
  return { data, error }
}

export const getTopPlayers = async (limit = 10) => {
  if (!isSupabaseConfigured()) {
    const scores = JSON.parse(localStorage.getItem('planetmoji_scores') || '[]')
    // Aggregate by user
    const userScores: Record<string, LeaderboardEntry & { totalScore: number }> = {}
    scores.forEach((s: LeaderboardEntry) => {
      if (!userScores[s.user_id]) {
        userScores[s.user_id] = { ...s, totalScore: 0 }
      }
      userScores[s.user_id].totalScore += s.score
    })
    return {
      data: Object.values(userScores)
        .sort((a, b) => b.totalScore - a.totalScore)
        .slice(0, limit),
      error: null
    }
  }

  // For Supabase, we'd need a view or function, but for now get recent top scores
  const { data, error } = await supabase
    .from('leaderboard')
    .select('*')
    .order('score', { ascending: false })
    .limit(limit)

  return { data, error }
}

// Profile functions
export const saveProfile = async (profile: Omit<UserProfile, 'id' | 'created_at' | 'updated_at'>) => {
  if (!isSupabaseConfigured()) {
    localStorage.setItem('planetmoji_profile', JSON.stringify(profile))
    return { data: profile, error: null }
  }

  const { data, error } = await supabase
    .from('profiles')
    .upsert([{ ...profile, updated_at: new Date().toISOString() }], { 
      onConflict: 'user_id' 
    })
    .select()
    .single()

  return { data, error }
}

export const getProfile = async (userId: string) => {
  if (!isSupabaseConfigured()) {
    const profile = localStorage.getItem('planetmoji_profile')
    return { data: profile ? JSON.parse(profile) : null, error: null }
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single()

  return { data, error }
}
